/**
 * Grafana Faro — Central Observability layer.
 *
 * Responsibilities:
 *  - Web Vitals / Core Web Vitals
 *  - Frontend RUM (Real User Monitoring)
 *  - Operational logs (structured, leveled)
 *  - OpenTelemetry browser traces
 *  - Fetch/XHR instrumentation
 *  - Navigation instrumentation
 *  - Trace context propagation for backend correlation
 *  - Error capture via pushError
 *  - Sanitization of sensitive data before send
 */
import {
  initializeFaro,
  getWebInstrumentations,
  type Faro,
  LogLevel,
} from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import type { TransportItem } from "@grafana/faro-web-sdk";
import { observabilityConfig } from "./env";

const cfg = observabilityConfig.grafana;

let faro: Faro | null = null;

// ── Sanitization helpers (private) ──────────────────────────────────────────

/** Check if event stacktrace originates from a browser extension. */
function isFromBrowserExtension(item: TransportItem): boolean {
  const payload = item.payload;
  const payloadStr = JSON.stringify(payload);
  return payloadStr.includes("chrome-extension://") || payloadStr.includes("moz-extension://");
}

/** Remove sensitive headers from event metadata/payload. */
function sanitizeHeaders(item: TransportItem): void {
  const sensitiveHeaders = ["Authorization", "authorization", "cookie", "Cookie", "x-api-key"];
  const payloadAny = item.payload as any;

  // Sanitize trace spans
  if (payloadAny?.resourceSpans) {
    const str = JSON.stringify(payloadAny.resourceSpans);
    let sanitized = str;
    for (const header of sensitiveHeaders) {
      const regex = new RegExp(`"${header}"\\s*:\\s*"[^"]*"`, "gi");
      sanitized = sanitized.replace(regex, `"${header}":"[REDACTED]"`);
    }
    if (sanitized !== str) {
      try {
        payloadAny.resourceSpans = JSON.parse(sanitized);
      } catch {
        // fail-open
      }
    }
  }
}

/** Redact sensitive query parameters in URLs. */
function sanitizeUrls(item: TransportItem): void {
  const sensitiveParams = ["token", "access_token", "apikey", "secret"];
  const str = JSON.stringify(item.payload);
  let sanitized = str;

  for (const param of sensitiveParams) {
    // Match param=value in query strings
    const regex = new RegExp(`(${param})=([^&"\\s]+)`, "gi");
    sanitized = sanitized.replace(regex, `$1=[REDACTED]`);
  }

  if (sanitized !== str) {
    try {
      (item as any).payload = JSON.parse(sanitized);
    } catch {
      // fail-open
    }
  }
}

/** Remove sensitive fields from request body payloads. */
function sanitizePayloads(item: TransportItem): void {
  const sensitiveFields = ["password", "secret", "token", "api_key"];
  const payloadAny = item.payload as any;

  if (payloadAny && typeof payloadAny === "object") {
    const str = JSON.stringify(payloadAny);
    let sanitized = str;

    for (const field of sensitiveFields) {
      const regex = new RegExp(`"${field}"\\s*:\\s*"[^"]*"`, "gi");
      sanitized = sanitized.replace(regex, `"${field}":"[REDACTED]"`);
    }

    if (sanitized !== str) {
      try {
        (item as any).payload = JSON.parse(sanitized);
      } catch {
        // fail-open
      }
    }
  }
}

/** Redact Supabase anon key from apikey header/param. */
function sanitizeSupabaseKey(item: TransportItem): void {
  const str = JSON.stringify(item.payload);
  // Redact apikey param and header value
  const regex = /apikey=([^&"\s]+)/gi;
  let sanitized = str.replace(regex, "apikey=[REDACTED]");

  // Also redact "apikey":"<value>" in headers
  const headerRegex = /"apikey"\s*:\s*"[^"]*"/gi;
  sanitized = sanitized.replace(headerRegex, '"apikey":"[REDACTED]"');

  if (sanitized !== str) {
    try {
      (item as any).payload = JSON.parse(sanitized);
    } catch {
      // fail-open
    }
  }
}

// ── beforeSend hook ─────────────────────────────────────────────────────────

function beforeSend(item: TransportItem): TransportItem | null {
  try {
    // 1. Filter browser extension events
    if (isFromBrowserExtension(item)) return null;

    // 2. Sanitize sensitive headers
    sanitizeHeaders(item);

    // 3. Sanitize URLs with sensitive query params
    sanitizeUrls(item);

    // 4. Sanitize body payloads
    sanitizePayloads(item);

    // 5. Sanitize Supabase anon key
    sanitizeSupabaseKey(item);
  } catch {
    // fail-open: return event without sanitization to not lose telemetry
  }

  return item;
}

// ── Initialization ──────────────────────────────────────────────────────────

export function initGrafana(): void {
  if (!cfg.enabled) return;

  try {
    faro = initializeFaro({
      url: cfg.url,
      app: {
        name: observabilityConfig.appName,
        version: observabilityConfig.appVersion,
        environment: observabilityConfig.environment,
      },

      sessionTracking: {
        samplingRate: cfg.sessionSampleRate,
      },

      instrumentations: [
        ...getWebInstrumentations({
          captureConsole: false,
        }),
        new TracingInstrumentation({
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: [
              // Propagate trace context to Supabase edge functions
              /db\.co/,
            ],
          },
        }),
      ],

      // Console instrumentation config at top level
      consoleInstrumentation: {
        disabledLevels: [LogLevel.DEBUG, LogLevel.TRACE, LogLevel.LOG],
      },

      beforeSend,
    });
  } catch (error) {
    console.error("[observability] Failed to initialize Grafana Faro:", error);
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

/** Get the Faro instance (null if not initialized). */
export function getFaro(): Faro | null {
  return faro;
}

/** Set user context for Grafana (call on login). Only sends ID, never email. */
export function setGrafanaUser(user: { id: string }): void {
  if (!faro) return;
  faro.api.setUser({ id: user.id });
}

/** Clear user context (call on logout). */
export function clearGrafanaUser(): void {
  if (!faro) return;
  faro.api.resetUser();
}

/** Push a structured log to Grafana Faro. */
export function pushLog(
  level: "info" | "warn" | "error",
  message: string,
  context?: Record<string, string>,
): void {
  if (!faro) return;

  const faroLevel =
    level === "error" ? LogLevel.ERROR
    : level === "warn" ? LogLevel.WARN
    : LogLevel.INFO;

  faro.api.pushLog([message], { level: faroLevel, context });
}

/** Push a custom event to Grafana Faro. */
export function pushEvent(
  name: string,
  attributes?: Record<string, string>,
): void {
  if (!faro) return;
  faro.api.pushEvent(name, attributes);
}

// ── Error Capture ───────────────────────────────────────────────────────────

/** Convert a Record<string, unknown> to Record<string, string>. */
function toStringRecord(obj?: Record<string, unknown>): Record<string, string> | undefined {
  if (!obj) return undefined;
  const result: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null) result[k] = String(v);
  }
  return result;
}

/** Capture an error and send it to Grafana Faro. */
export function captureError(error: unknown, context?: Record<string, unknown>): void {
  if (!faro) {
    console.error("[captureError]", error, context);
    return;
  }

  const errorObj = error instanceof Error ? error : new Error(String(error));
  faro.api.pushError(errorObj);
  pushLog("error", errorObj.message, toStringRecord(context));
}
